import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';

interface AnalyticsData {
  summary: {
    totalRequests: number;
    visionRequests: number;
    valueRequests: number;
    avgResponseTime: number;
    successfulRequests: number;
    failedRequests: number;
    successRate: number;
  };
  daily: Array<{
    date: string;
    requests: number;
    avg_response_time: number;
  }>;
  endpoints: Array<{
    endpoint: string;
    requests: number;
    avg_response_time: number;
    errors: number;
  }>;
  recentRequests: Array<{
    id: string;
    endpoint: string;
    method: string;
    status_code: number;
    response_time_ms: number;
    created_at: string;
  }>;
  products: {
    total: number;
    active: number;
    embedded: number;
    pendingEmbeddings: number;
  };
  apiKeys: {
    total: number;
    active: number;
  };
  lifecycle: {
    totalEvents: number;
    totalThings: number;
    newThingsInPeriod: number;
    eventsByType: Array<{ event_type: string; count: number }>;
    dailyEvents: Array<{ date: string; events: number }>;
    stageDistribution: Array<{ lifecycle_stage: string; count: number }>;
    statusDistribution: Array<{ current_status: string; count: number }>;
  };
  period: string;
}

export const Route = createFileRoute('/analytics')({
  component: Analytics,
});

function Analytics() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7d');

  useEffect(() => {
    async function fetchAnalytics() {
      setLoading(true);
      try {
        const response = await fetch(`/api/analytics?period=${period}`);
        if (response.ok) {
          const result = await response.json();
          setData(result);
        }
      } catch (err) {
        console.error('Failed to fetch analytics:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchAnalytics();
  }, [period]);

  const formatNumber = (n: number) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light" style={{ color: '#171717' }}>Analytics</h1>
          <p className="text-base mt-1" style={{ color: '#A9A9A9' }}>
            API usage, performance, and insights
          </p>
        </div>
        <select 
          className="px-4 py-2 border  bg-white text-sm"
          style={{ borderColor: 'rgba(169,169,169,0.3)', color: '#171717' }}
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
          <option value="120d">Last 120 days</option>
          <option value="all">All time</option>
        </select>
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#00FBA9', borderTopColor: 'transparent' }} />
        </div>
      ) : (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <MetricCard 
              label="Total API Calls" 
              value={formatNumber(data?.summary.totalRequests || 0)}
              subtext={`${data?.summary.successRate || 100}% success rate`}
              trend={data?.summary.totalRequests ? 'up' : 'neutral'}
            />
            <MetricCard 
              label="Vision Requests" 
              value={formatNumber(data?.summary.visionRequests || 0)}
              subtext="Image analysis"
            />
            <MetricCard 
              label="Value Requests" 
              value={formatNumber(data?.summary.valueRequests || 0)}
              subtext="Pricing lookups"
            />
            <MetricCard 
              label="Avg Response Time" 
              value={data?.summary.avgResponseTime ? `${data.summary.avgResponseTime}ms` : '--'}
              subtext="P50 latency"
            />
          </div>

          {/* Resource Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="card">
              <h3 className="text-sm font-medium mb-3" style={{ color: '#A9A9A9' }}>Products</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-light" style={{ color: '#171717' }}>
                  {data?.products.total || 0}
                </span>
                <span className="text-sm" style={{ color: '#A9A9A9' }}>total</span>
              </div>
              <div className="mt-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#A9A9A9' }}>Active</span>
                  <span style={{ color: '#171717' }}>{data?.products.active || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#A9A9A9' }}>Embedded</span>
                  <span style={{ color: '#00FBA9' }}>{data?.products.embedded || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#A9A9A9' }}>Pending</span>
                  <span style={{ color: '#FF4C00' }}>{data?.products.pendingEmbeddings || 0}</span>
                </div>
              </div>
            </div>
            
            <div className="card">
              <h3 className="text-sm font-medium mb-3" style={{ color: '#A9A9A9' }}>API Keys</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-light" style={{ color: '#171717' }}>
                  {data?.apiKeys.active || 0}
                </span>
                <span className="text-sm" style={{ color: '#A9A9A9' }}>active</span>
              </div>
              <div className="mt-3">
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#A9A9A9' }}>Total created</span>
                  <span style={{ color: '#171717' }}>{data?.apiKeys.total || 0}</span>
                </div>
              </div>
            </div>
            
            <div className="card">
              <h3 className="text-sm font-medium mb-3" style={{ color: '#A9A9A9' }}>Errors</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-light" style={{ color: data?.summary.failedRequests ? '#FF4C00' : '#171717' }}>
                  {data?.summary.failedRequests || 0}
                </span>
                <span className="text-sm" style={{ color: '#A9A9A9' }}>failed requests</span>
              </div>
              <div className="mt-3">
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#A9A9A9' }}>Successful</span>
                  <span style={{ color: '#00FBA9' }}>{data?.summary.successfulRequests || 0}</span>
                </div>
              </div>
            </div>
          </div>
      
          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card">
              <h3 className="text-base font-medium mb-4" style={{ color: '#171717' }}>API Calls Over Time</h3>
              {data?.daily && data.daily.length > 0 ? (
                <div className="h-64">
                  <SimpleBarChart data={data.daily} />
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center" style={{ color: '#A9A9A9' }}>
                  <div className="text-center">
                    <p className="text-sm">No API calls yet</p>
                    <p className="text-xs mt-1">Make your first API call to see data here</p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="card">
              <h3 className="text-base font-medium mb-4" style={{ color: '#171717' }}>Requests by Endpoint</h3>
              {data?.endpoints && data.endpoints.length > 0 ? (
                <div className="space-y-3">
                  {data.endpoints.map((ep, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-mono truncate" style={{ color: '#171717' }}>{ep.endpoint}</span>
                          <span className="text-sm" style={{ color: '#A9A9A9' }}>{ep.requests}</span>
                        </div>
                        <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#FAFAFA' }}>
                          <div 
                            className="h-full rounded-full"
                            style={{ 
                              backgroundColor: '#00FBA9',
                              width: `${(ep.requests / data.endpoints[0].requests) * 100}%`
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center" style={{ color: '#A9A9A9' }}>
                  <div className="text-center">
                    <p className="text-sm">No endpoint data yet</p>
                    <p className="text-xs mt-1">Endpoint usage will appear here</p>
                  </div>
                </div>
              )}
            </div>
          </div>
      
          {/* Lifecycle Analytics Section */}
          {((data?.lifecycle?.totalEvents ?? 0) > 0 || (data?.lifecycle?.totalThings ?? 0) > 0) && (
            <>
              <div className="pt-6 border-t" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
                <h2 className="text-xl font-light mb-4" style={{ color: '#171717' }}>Lifecycle Analytics</h2>
                <p className="text-sm mb-6" style={{ color: '#A9A9A9' }}>
                  Product lifecycle tracking from canonical events
                </p>
              </div>
              
              {/* Lifecycle KPIs */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <MetricCard 
                  label="Total Events" 
                  value={formatNumber(data?.lifecycle.totalEvents || 0)}
                  subtext="Canonical events recorded"
                />
                <MetricCard 
                  label="Total Items" 
                  value={formatNumber(data?.lifecycle.totalThings || 0)}
                  subtext="Things tracked"
                />
                <MetricCard 
                  label="New This Period" 
                  value={formatNumber(data?.lifecycle?.newThingsInPeriod || 0)}
                  subtext={`Added in last ${period === '7d' ? '7 days' : period === '30d' ? '30 days' : period === '90d' ? '90 days' : 'all time'}`}
                  trend={(data?.lifecycle?.newThingsInPeriod ?? 0) > 0 ? 'up' : 'neutral'}
                />
              </div>
              
              {/* Lifecycle Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Event Volume Over Time */}
                <div className="card">
                  <h3 className="text-base font-medium mb-4" style={{ color: '#171717' }}>Events Over Time</h3>
                  {data?.lifecycle.dailyEvents && data.lifecycle.dailyEvents.length > 0 ? (
                    <div className="h-64">
                      <SimpleBarChart data={data.lifecycle.dailyEvents.map(d => ({ date: d.date, requests: d.events }))} />
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center" style={{ color: '#A9A9A9' }}>
                      <div className="text-center">
                        <p className="text-sm">No events yet</p>
                        <p className="text-xs mt-1">Events will appear as items flow through the system</p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Lifecycle Stage Distribution */}
                <div className="card">
                  <h3 className="text-base font-medium mb-4" style={{ color: '#171717' }}>Lifecycle Stages</h3>
                  {data?.lifecycle.stageDistribution && data.lifecycle.stageDistribution.length > 0 ? (
                    <div className="space-y-3">
                      {data.lifecycle.stageDistribution.map((stage, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="flex-1">
                            <div className="flex justify-between mb-1">
                              <span className="text-sm capitalize" style={{ color: '#171717' }}>
                                {stage.lifecycle_stage?.replace(/_/g, ' ') || 'Unknown'}
                              </span>
                              <span className="text-sm" style={{ color: '#A9A9A9' }}>{stage.count}</span>
                            </div>
                            <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#FAFAFA' }}>
                              <div 
                                className="h-full rounded-full"
                                style={{ 
                                  backgroundColor: getStageColor(i),
                                  width: `${(stage.count / data.lifecycle.stageDistribution[0].count) * 100}%`
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center" style={{ color: '#A9A9A9' }}>
                      <div className="text-center">
                        <p className="text-sm">No lifecycle data yet</p>
                        <p className="text-xs mt-1">Items will be tracked as they progress</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Event Types Breakdown */}
              <div className="card">
                <h3 className="text-base font-medium mb-4" style={{ color: '#171717' }}>Event Types</h3>
                {data?.lifecycle.eventsByType && data.lifecycle.eventsByType.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {data.lifecycle.eventsByType.map((evt, i) => (
                      <div 
                        key={i} 
                        className="p-3  text-center"
                        style={{ backgroundColor: '#FAFAFA' }}
                      >
                        <div className="text-lg font-medium" style={{ color: '#171717' }}>
                          {evt.count}
                        </div>
                        <div className="text-xs mt-1 truncate" style={{ color: '#A9A9A9' }} title={evt.event_type}>
                          {formatEventType(evt.event_type)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8" style={{ color: '#A9A9A9' }}>
                    <p className="text-sm">No events recorded yet</p>
                  </div>
                )}
              </div>
            </>
          )}
      
          {/* Recent Requests */}
          <div className="card">
            <h3 className="text-base font-medium mb-4" style={{ color: '#171717' }}>Recent API Requests</h3>
            {data?.recentRequests && data.recentRequests.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b" style={{ borderColor: 'rgba(169,169,169,0.1)' }}>
                      <th className="px-4 py-2 text-left font-medium" style={{ color: '#A9A9A9' }}>Endpoint</th>
                      <th className="px-4 py-2 text-left font-medium" style={{ color: '#A9A9A9' }}>Method</th>
                      <th className="px-4 py-2 text-left font-medium" style={{ color: '#A9A9A9' }}>Status</th>
                      <th className="px-4 py-2 text-left font-medium" style={{ color: '#A9A9A9' }}>Latency</th>
                      <th className="px-4 py-2 text-left font-medium" style={{ color: '#A9A9A9' }}>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recentRequests.map((req) => (
                      <tr key={req.id} className="border-b" style={{ borderColor: 'rgba(169,169,169,0.1)' }}>
                        <td className="px-4 py-3 font-mono text-xs" style={{ color: '#171717' }}>{req.endpoint}</td>
                        <td className="px-4 py-3">
                          <span 
                            className="pill text-xs"
                            style={{ 
                              backgroundColor: req.method === 'GET' ? 'rgba(0,251,169,0.1)' : 'rgba(255,76,0,0.1)',
                              color: req.method === 'GET' ? '#00FBA9' : '#FF4C00'
                            }}
                          >
                            {req.method}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span style={{ color: req.status_code < 400 ? '#00FBA9' : '#FF4C00' }}>
                            {req.status_code}
                          </span>
                        </td>
                        <td className="px-4 py-3" style={{ color: '#A9A9A9' }}>{req.response_time_ms}ms</td>
                        <td className="px-4 py-3" style={{ color: '#A9A9A9' }}>
                          {new Date(req.created_at).toLocaleTimeString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8" style={{ color: '#A9A9A9' }}>
                <p className="text-sm">No requests recorded yet</p>
                <p className="text-xs mt-1">API requests will be logged here</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function MetricCard({ 
  label, 
  value, 
  subtext,
  trend 
}: { 
  label: string; 
  value: string;
  subtext?: string;
  trend?: 'up' | 'down' | 'neutral';
}) {
  return (
    <div className="card">
      <p className="text-sm" style={{ color: '#A9A9A9' }}>{label}</p>
      <p className="text-3xl font-light mt-2" style={{ color: '#171717' }}>{value}</p>
      {subtext && (
        <p className="text-xs mt-1" style={{ color: trend === 'up' ? '#00FBA9' : trend === 'down' ? '#FF4C00' : '#A9A9A9' }}>
          {trend === 'up' && '↑ '}{trend === 'down' && '↓ '}{subtext}
        </p>
      )}
    </div>
  );
}

// Helper to get colors for lifecycle stages
function getStageColor(index: number): string {
  const colors = ['#00FBA9', '#00D4FF', '#FF4C00', '#FFB800', '#8B5CF6', '#EC4899'];
  return colors[index % colors.length];
}

// Helper to format event types for display
function formatEventType(eventType: string): string {
  if (!eventType) return 'Unknown';
  // Convert PRODUCT_CREATED -> Product Created
  return eventType
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Simple bar chart component (no external dependencies)
function SimpleBarChart({ data }: { data: Array<{ date: string; requests: number }> }) {
  if (!data || data.length === 0) return null;
  
  const maxValue = Math.max(...data.map(d => d.requests), 1);
  
  return (
    <div className="flex items-end h-full gap-1 pb-6">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div 
            className="w-full rounded-t transition-all hover:opacity-80"
            style={{ 
              height: `${(d.requests / maxValue) * 100}%`,
              minHeight: d.requests > 0 ? '4px' : '0',
              backgroundColor: '#00FBA9'
            }}
            title={`${d.date}: ${d.requests} requests`}
          />
          <span className="text-xs rotate-45 origin-left whitespace-nowrap" style={{ color: '#A9A9A9' }}>
            {new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        </div>
      ))}
    </div>
  );
}
